<template>
  <!--
    Закрытие архивирует итоги и очищает оперативные данные кампании, поэтому
    требует отсутствия незавершённых сроков и ручного ввода подтверждаемого года.
  -->
  <v-card class="close-admission" elevation="0">
    <v-card-title class="close-admission__header">
      <div>
        <h2 class="close-admission__title">Закрытие приёмной комиссии</h2>
        <p class="close-admission__description">
          Сохранение итоговой статистики и завершение текущей приёмной кампании.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="close-admission__content">
      <v-alert
        v-if="error"
        class="close-admission__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="successMessage"
        class="close-admission__alert"
        type="success"
        variant="tonal"
        closable
        @click:close="successMessage = ''"
      >
        {{ successMessage }}
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader type="heading, paragraph, article" />
      </div>

      <template v-else-if="state">
        <v-alert
          v-if="state.closed"
          type="success"
          variant="tonal"
          icon="mdi-check-decagram-outline"
        >
          Приёмная комиссия {{ state.year }} года завершена. Итоговая статистика
          сохранена.
        </v-alert>

        <template v-else>
          <v-alert
            class="close-admission__warning"
            type="warning"
            variant="tonal"
            icon="mdi-alert-outline"
          >
            Закрытие приёмной комиссии необратимо. Данные текущей кампании будут
            удалены после сохранения общей статистики за год.
          </v-alert>

          <div class="close-admission__summary">
            <div class="close-admission__summary-item">
              <span>Приёмная кампания</span>
              <strong>{{ state.year }}</strong>
            </div>
            <div class="close-admission__summary-item">
              <span>Текущих заявлений</span>
              <strong>{{ state.applicationCount }}</strong>
            </div>
            <div class="close-admission__summary-item close-admission__summary-item--danger">
              <span>Незачисленных личных дел</span>
              <strong>{{ state.unadmittedCount }}</strong>
            </div>
          </div>

          <v-alert
            v-if="state.blockingPeriods.length"
            class="close-admission__periods"
            type="info"
            variant="tonal"
          >
            <strong>Закрытие пока недоступно.</strong>
            <div class="mt-1">
              По следующим формам обучения приём ещё не завершён:
            </div>
            <ul>
              <li
                v-for="period in state.blockingPeriods"
                :key="period.id"
              >
                {{ period.studyName }} — до {{ period.endDate }}
              </li>
            </ul>
          </v-alert>

          <div class="close-admission__danger-zone">
            <div class="close-admission__danger-content">
              <v-icon icon="mdi-database-alert-outline" size="30" />
              <div>
                <strong>Завершить кампанию {{ state.year }} года</strong>
                <span>
                  Будут очищены заявления, календарь, протоколы и служебный журнал.
                  Незачисленные личные дела будут удалены.
                </span>
              </div>
            </div>

            <v-btn
              color="error"
              variant="flat"
              prepend-icon="mdi-calendar-remove-outline"
              :disabled="!state.canClose"
              @click="openConfirmation"
            >
              Закрыть приёмную комиссию
            </v-btn>
          </div>
        </template>
      </template>
    </v-card-text>

    <v-dialog v-model="confirmationDialog" max-width="620" persistent>
      <v-card>
        <v-card-title class="close-admission__dialog-title">
          Подтвердите закрытие приёмной комиссии
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="dialogError"
            class="mb-5"
            type="error"
            variant="tonal"
          >
            {{ dialogError }}
          </v-alert>

          <p>
            После выполнения операции восстановить данные кампании через
            конфигуратор будет невозможно.
          </p>
          <ul class="close-admission__confirmation-list">
            <li>итоговая статистика будет сохранена за {{ stateYear }} год;</li>
            <li>данные текущих заявлений и календаря будут очищены;</li>
            <li>незачисленные личные дела будут удалены.</li>
          </ul>

          <v-text-field
            v-model.trim="confirmation"
            class="mt-5"
            :label="`Для подтверждения введите ${stateYear}`"
            variant="outlined"
            inputmode="numeric"
            autocomplete="off"
            hide-details="auto"
            :disabled="closing"
            @keydown.enter="closeCampaign"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="closing" @click="closeConfirmation">
            Отмена
          </v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="closing"
            :disabled="!confirmationMatches"
            @click="closeCampaign"
          >
            Закрыть без возможности восстановления
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'CloseAdmissionSection',
  data() {
    return {
      state: null,
      loading: true,
      closing: false,
      error: '',
      dialogError: '',
      successMessage: '',
      confirmationDialog: false,
      confirmation: ''
    };
  },
  computed: {
    stateYear() {
      return this.state ? Number(this.state.year) : 0;
    },
    confirmationMatches() {
      // Ввод года защищает от случайного подтверждения необратимой операции.
      return this.confirmation === String(this.stateYear);
    }
  },
  created() {
    this.loadState();
  },
  methods: {
    loadState() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getAdmissionClosureStatus'
        )
        .then((response) => {
          this.state = response.data.state || null;
        })
        .catch((error) => {
          this.error = this.getErrorMessage(
            error,
            'Не удалось загрузить состояние приёмной комиссии.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openConfirmation() {
      if (!this.state || !this.state.canClose) {
        return;
      }

      this.confirmation = '';
      this.dialogError = '';
      this.confirmationDialog = true;
    },
    closeConfirmation() {
      this.confirmationDialog = false;
      this.confirmation = '';
      this.dialogError = '';
    },
    closeCampaign() {
      if (!this.confirmationMatches || this.closing) {
        return;
      }

      this.closing = true;
      this.dialogError = '';
      this.successMessage = '';

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/closeAdmissionCampaign',
          new URLSearchParams({
            confirmationYear: String(this.stateYear)
          })
        )
        .then((response) => {
          this.state = response.data.state || this.state;
          this.successMessage = response.data.message
            || 'Приёмная комиссия завершена успешно.';
          this.closeConfirmation();
        })
        .catch((error) => {
          this.dialogError = this.getErrorMessage(
            error,
            'Не удалось закрыть приёмную комиссию.'
          );
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.closing = false;
        });
    },
    getErrorMessage(error, fallback) {
      const responseData = error && error.response
        ? error.response.data
        : null;

      if (typeof responseData === 'string' && responseData.trim()) {
        return responseData;
      }
      if (responseData && typeof responseData.message === 'string') {
        return responseData.message;
      }

      return fallback;
    }
  }
};
</script>

<style scoped>
.close-admission {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.close-admission__header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.close-admission__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
}

.close-admission__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.close-admission__content {
  padding: 24px 28px 28px;
}

.close-admission__alert,
.close-admission__warning,
.close-admission__periods {
  margin-bottom: 20px;
}

.close-admission__summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.close-admission__summary-item {
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #f8fafc;
}

.close-admission__summary-item span,
.close-admission__summary-item strong {
  display: block;
}

.close-admission__summary-item span {
  margin-bottom: 4px;
  color: #667085;
  font-size: 13px;
}

.close-admission__summary-item strong {
  color: #344054;
  font-size: 22px;
}

.close-admission__summary-item--danger strong {
  color: #b42318;
}

.close-admission__periods ul {
  margin: 8px 0 0;
}

.close-admission__danger-zone {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 20px;
  border: 1px solid #fda29b;
  border-radius: 10px;
  background: #fff7f6;
}

.close-admission__danger-content {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  color: #b42318;
}

.close-admission__danger-content strong,
.close-admission__danger-content span {
  display: block;
}

.close-admission__danger-content span {
  margin-top: 4px;
  color: #667085;
  font-size: 13px;
  line-height: 1.45;
}

.close-admission__dialog-title {
  white-space: normal;
}

.close-admission__confirmation-list {
  margin: 12px 0 0;
}

@media (max-width: 760px) {
  .close-admission__summary {
    grid-template-columns: 1fr;
  }

  .close-admission__danger-zone {
    align-items: stretch;
    flex-direction: column;
  }
}

@media (max-width: 600px) {
  .close-admission__header,
  .close-admission__content {
    padding-right: 18px;
    padding-left: 18px;
  }
}
</style>
